import { z } from 'zod';

/**
 * Renderable widget vocabulary the chatbot LLM may emit — the single source of
 * truth, co-owned by the backend validator (`render_widget` tool + the run_code
 * result mapper) and the web renderer (`WidgetRenderer.vue`). See
 * `docs/copilot/widgets.md`.
 *
 * Each variant maps 1:1 to an existing component. Specs are small, serializable,
 * and display-only — a widget's numbers/series must come from real tool/code
 * output, never authored inline by the model (widgets.md §2). Written in zod 4 to
 * match the backend (the validation authority); the web app consumes the inferred
 * TS types (structural, zod-version-agnostic).
 *
 * The wire shape is `{ type, props }` (`WidgetSpec`). The per-type schemas below are written
 * FLAT (`{ type, ...fields }`) because that is the persisted legacy shape (`LegacyWidgetSpec`),
 * and `WIDGET_PROPS` is derived from them by omitting `type` — so the two shapes cannot drift.
 */

/**
 * The unit a percentage value is in. THE FAILURE IT PREVENTS (test audit C-207): the model sent
 * percent points and 0..1 fractions under the same `percentage`, and a gross margin of 0.74 was
 * drawn as "0.74%". A fraction is drawn times 100.
 */
const percentUnit = z
  .enum(['points', 'fraction'])
  .describe('"points": 12.5 means 12.5%. "fraction": 0.125 means 12.5% (drawn times 100)');

const widgetBase = z.object({
  /** Optional caption rendered above the widget. */
  title: z.string().max(120).optional(),
});

/** One KPI tile -> `StatCard.vue`. */
export const statCardSpec = widgetBase.extend({
  type: z.literal('stat_card'),
  label: z.string().max(80),
  value: z.union([z.number(), z.string()]),
  change: z.number().optional(),
  changePercent: z.number().optional(),
  format: z
    .enum(['number', 'currency', 'percentage'])
    .optional()
    .describe('percentage: set `percentUnit` too. currency: set `currency` too'),
  /**
   * OPTIONAL here, unlike a table column's: apps/web validates this schema with zod 3, whose
   * discriminated union takes plain objects only, so a tile cannot make it conditionally required.
   * Omitted, the value is read as points: 94 % of stored percentage tiles hold a value over 1.
   */
  percentUnit: percentUnit.optional(),
  /** Without it a `currency` value is drawn with no symbol — never in a guessed USD (test audit C-80). */
  currency: z
    .string()
    .length(3)
    .optional()
    .describe('ISO 4217 code of a currency value, e.g. "PLN"; omitted, the figure shows no symbol'),
  prefix: z.string().max(4).optional(),
  suffix: z.string().max(4).optional(),
  colorTheme: z.enum(['auto', 'positive', 'negative', 'neutral']).optional(),
  benchmark: z
    .object({
      label: z.string().max(80).describe('What the reference figure is, e.g. "5Y avg"'),
      value: z.string().max(40).describe('The reference figure, pre-formatted, e.g. "24.1"'),
    })
    .optional()
    .describe('Optional comparison line under the value, e.g. "5Y avg 24.1"'),
  spark: z
    .array(z.number())
    .min(2)
    .max(50)
    .optional()
    .describe('Optional inline trend: 2..50 raw values, oldest first, drawn as a sparkline'),
});

/** 1–8 KPIs in a grid -> a row of `StatCard.vue` ("metrics overview"). */
export const statGroupSpec = widgetBase.extend({
  type: z.literal('stat_group'),
  /** Cap 6 -> 8: a 7th KPI is a COSMETIC overflow, and rejecting the whole group over it killed live stat_group calls. */
  stats: z
    .array(statCardSpec.omit({ type: true }))
    .min(1)
    .max(8),
});

/** Recognized magnitude keys on a shape-A point object (the value the chart plots). */
export const POINT_VALUE_KEYS = ['value', 'y', 'amount', 'percent', 'count', 'weight'] as const;

/**
 * A cell counts as a plottable number if it's a finite `number` OR a non-blank string that
 * parses to one — mirrors the renderer's `Number(...)` coercion so validation ⇔ renderability.
 */
function isRenderableNumber(v: unknown): boolean {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string') {
    const t = v.trim();
    return t !== '' && Number.isFinite(Number(t));
  }
  return false;
}

/**
 * One array item the renderer (`chart-option.ts` `toPoints`) can plot: a bare number / numeric
 * string (shape C), a `[label, number]` tuple of arity ≥ 2 (shape B), or a point object exposing
 * a numeric value key (shape A). The renderer SALVAGES the valid subset and skips the rest, so an
 * array validates when AT LEAST ONE item is plottable (an all-garbage array is still rejected).
 */
function isPlottableItem(item: unknown): boolean {
  if (isRenderableNumber(item)) return true;
  if (Array.isArray(item)) return item.length >= 2 && isRenderableNumber(item[1]);
  if (item !== null && typeof item === 'object') {
    const o = item as Record<string, unknown>;
    return POINT_VALUE_KEYS.some((k) => isRenderableNumber(o[k]));
  }
  return false;
}

/**
 * Chart `data` — the LENIENT contract co-owned by the backend validator and the web renderer
 * (`features/chatbot/.../chart-option.ts` `toPoints` + `toEchartsOption`). Tuned so that
 * validation passes IFF the renderer draws a NON-EMPTY chart — in BOTH directions: it mirrors
 * the renderer's numeric-STRING coercion, partial-array salvage, tuple arity, and F-before-E
 * precedence, so a renderable payload is never rejected AND a rejected payload never renders a
 * silent "No data available" card (it's reported back for self-correction instead).
 *
 * REJECTS a bare string/number/boolean/null/undefined, an empty or all-garbage array, an empty
 * object, and an object map with no numeric value.
 *
 * - A/B/C. A non-empty array with ≥ 1 plottable item (`isPlottableItem`): point objects
 *   `{ <label?>, <value> }` (label ∈ {name,label,category,x,date,symbol,key}; value ∈
 *   {value,y,amount,percent,count,weight}), `[label, number]` tuples (arity ≥ 2), or bare
 *   numbers (index-labeled 1..n). Values may be finite numbers OR numeric strings.
 * - F. ECharts option: any object carrying a NON-EMPTY `series` ARRAY (+ optional `xAxis`),
 *   passed STRAIGHT THROUGH to `ChartWrapper`'s option. Tried BEFORE E — the renderer keys F/E
 *   off the VALUE being an array, so a scalar `series` key (e.g. `{ series: 5 }`) is NOT F and
 *   falls through to a shape-D map, exactly as the renderer treats it.
 * - E. Chart.js: `{ labels: (string|number)[], datasets: [{ label?, data }] }` (> 1 dataset ⇒
 *   multi-series; dataset `data` may contain numeric strings).
 * - D. `{ [label]: number }` — a NON-EMPTY map with ≥ 1 plottable value (non-numeric entries
 *   are salvaged/skipped by the renderer).
 */
export const ChartData = z.union([
  // A/B/C — a non-empty array with at least one plottable item (partial salvage OK).
  z
    .array(z.unknown())
    .min(1)
    .refine((arr) => arr.some(isPlottableItem), {
      message: 'chart data array needs a plottable point, [label, number] tuple, or number',
    }),
  // F — ECharts option object (pass-through): a NON-EMPTY `series` array. Before E, matching the renderer.
  // `.catchall(z.unknown())`, NOT `z.looseObject()` / `.loose()`: apps/web bundles this SOURCE and
  // resolves zod v3 (`apps/web/package.json` pins zod ^3.24, which has no `looseObject`), which
  // made this module throw at load and brick ALL widget validation. `catchall` is the spelling
  // both majors share — same semantics (unknown keys KEPT), and unlike `.passthrough()` it is not
  // deprecated in zod v4.
  z.object({ series: z.array(z.unknown()).min(1) }).catchall(z.unknown()),
  // E — Chart.js { labels, datasets:[{ label?, data }] } (data may be numeric strings).
  z.object({
    labels: z.array(z.union([z.string(), z.number()])).min(1),
    datasets: z
      .array(
        z.object({
          label: z.string().optional(),
          data: z
            .array(z.unknown())
            .min(1)
            .refine((d) => d.some(isRenderableNumber), { message: 'dataset.data needs a number' }),
        }),
      )
      .min(1),
  }),
  // D — non-empty { label: number } map with ≥1 plottable value (array/empty rejected).
  z
    .record(z.string(), z.unknown())
    .refine((o) => !Array.isArray(o) && Object.values(o).some(isRenderableNumber), {
      message: 'chart data object map needs at least one numeric value',
    }),
]);
/** The accepted chart-data shapes (see `ChartData`). */
export type ChartData = z.infer<typeof ChartData>;

/**
 * Chart -> `ChartWrapper.vue`. `data` is validated by `ChartData` (the lenient six-shape
 * contract), then coerced by the web renderer (`chart-option.ts`). `options`/`height` pass
 * straight through.
 */
export const chartSpec = widgetBase.extend({
  type: z.literal('chart'),
  chartType: z.enum(['line', 'bar', 'pie', 'treemap', 'sunburst']),
  data: ChartData,
  options: z.record(z.string(), z.unknown()).optional(),
  height: z.union([z.number(), z.string()]).optional(),
});

const tableColumnBase = z.object({
  key: z.string(),
  label: z.string(),
  align: z.enum(['left', 'center', 'right']).optional(),
});

/**
 * A table column: a MONEY column names its currency, a PERCENTAGE column its unit, any other
 * format carries neither.
 *
 * THE FAILURE THIS PREVENTS (test audit C-80): the column had no currency field, so the renderer
 * drew every `currency` column in USD and a Polish user's PLN book read "$189,685.89". A union
 * makes the pair impossible to half-state: `format: 'currency'` without `currency` is a type
 * error in every writer and a schema refusal on the wire (the healer draws such a column as a
 * plain number rather than refuse the table — `normalizeTable`). A `percentage` column states its
 * `percentUnit` the same way (C-207); the healer reads a stored column's unit off its values.
 */
const tableColumn = z.union([
  tableColumnBase.extend({
    format: z.literal('currency'),
    currency: z
      .string()
      .length(3)
      .describe(
        'ISO 4217 code of this column\'s money figures, e.g. "PLN" — the book\'s own currency',
      ),
  }),
  tableColumnBase.extend({ format: z.literal('percentage'), percentUnit }),
  tableColumnBase.extend({ format: z.enum(['number', 'text']).optional() }),
]);

/** Generic table -> `DataTable.vue`. */
export const tableSpec = widgetBase.extend({
  type: z.literal('table'),
  columns: z
    .array(tableColumn)
    .min(1)
    /** Cap 12 -> 16: a 13th column is a COSMETIC overflow (the table scrolls); refusing to draw it lost the whole table. */
    .max(16),
  rows: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.null()]))).max(200),
});

/**
 * Analyzed news articles -> list of `ArticleCard.vue`. `articles` is
 * `NewsArticleDto[]` straight from the news tools (trusted from typed tool
 * output; re-declaring the deep DTO in zod is brittle).
 */
export const newsListSpec = widgetBase.extend({
  type: z.literal('news_list'),
  articles: z.array(z.unknown()).max(20),
});

/** Highlighted insight / warning -> `Card.vue` + `Badge.vue`. */
export const calloutSpec = widgetBase.extend({
  type: z.literal('callout'),
  variant: z.enum(['info', 'success', 'warning', 'error', 'neutral']),
  text: z.string().max(600),
});

/**
 * Interactive quick-pick options -> `ChoicesWidget.vue`. The ONE interactive widget:
 * clicking an option sends `value ?? label` back as the user's next message, so the
 * copilot can offer a choice the user clicks instead of typing. Use it whenever you'd
 * otherwise ask the user to pick between named options (which portfolio? which action?).
 */
export const choicesSpec = widgetBase.extend({
  type: z.literal('choices'),
  prompt: z.string().max(200).optional(),
  options: z
    .array(
      z.object({
        label: z.string().max(80),
        /** Sent on click; defaults to `label`. Use it to send a fuller instruction. */
        value: z.string().max(400).optional(),
        description: z.string().max(160).optional(),
      }),
    )
    .min(1)
    /** Cap 6 -> 8: a 7th option is a COSMETIC overflow; rejecting the pick list made the copilot tell the user `choices` was unsupported. */
    .max(8),
});

/**
 * Criteria checklist with a score -> `ScorecardWidget.vue` (rows + `LevelMeter`).
 * The "valuation check" panel: named criteria each judged met/not-met, plus an
 * optional n-of-N score.
 */
export const scorecardSpec = widgetBase.extend({
  type: z.literal('scorecard'),
  score: z
    .object({
      value: z.number().describe('How many points were scored'),
      max: z.number().describe('The maximum possible score'),
    })
    .optional()
    .describe('Optional overall score; omitted, the met-criteria count is shown instead'),
  criteria: z
    .array(
      z.object({
        label: z.string().min(1).max(120).describe('What the criterion checks'),
        met: z.boolean().describe('Whether the criterion passed'),
        detail: z
          .string()
          .max(200)
          .optional()
          .describe('Optional one-line evidence, e.g. "P/E 21.4 vs 5Y avg 24.1"'),
      }),
    )
    .min(1)
    .max(12)
    .describe('1..12 pass/fail rows'),
});

/** One bounded reading on a dial -> `GaugeWidget.vue` (`RadialGauge`). */
export const gaugeSpec = widgetBase.extend({
  type: z.literal('gauge'),
  label: z.string().max(80).optional().describe('What the reading measures, e.g. "RSI (14d)"'),
  value: z.number().describe('The current reading'),
  min: z.number().describe('Bottom of the scale'),
  max: z.number().describe('Top of the scale; must exceed min'),
  thresholds: z
    .array(
      z.object({
        value: z.number().describe('Where on the scale the threshold sits'),
        label: z.string().max(40).optional().describe('What crossing it means, e.g. "overbought"'),
      }),
    )
    .max(4)
    .optional()
    .describe('Up to 4 reference points shown beside the gauge'),
});

/** A value inside a band -> `RangeIndicatorWidget.vue` (`RangeIndicator`). */
export const rangeIndicatorSpec = widgetBase.extend({
  type: z.literal('range_indicator'),
  label: z.string().max(80).optional().describe('What the value is, e.g. "Current price"'),
  value: z.number().describe('Where the subject sits today'),
  low: z.number().describe('Bottom of the band'),
  high: z.number().describe('Top of the band; must exceed low'),
  lowLabel: z.string().max(40).optional().describe('Name of the low end, e.g. "52w low"'),
  highLabel: z.string().max(40).optional().describe('Name of the high end, e.g. "52w high"'),
});

/** A metric vs its pass bar -> `ThresholdBarWidget.vue` (`ThresholdBar`). */
export const thresholdBarSpec = widgetBase.extend({
  type: z.literal('threshold_bar'),
  label: z.string().max(80).optional().describe('What the metric is, e.g. "P/E"'),
  value: z.number().describe('The measured value'),
  threshold: z.number().describe('The qualifying bar the value is judged against'),
  thresholdLabel: z
    .string()
    .max(40)
    .optional()
    .describe('Name of the threshold, e.g. "5Y avg" or "sector median"'),
  betterWhen: z
    .enum(['below', 'above'])
    .describe('Which side of the threshold is good — colors the fill'),
});

/** Weight breakdown of a whole -> `AllocationWidget.vue` (`AllocationBar`). */
export const allocationSpec = widgetBase.extend({
  type: z.literal('allocation'),
  items: z
    .array(
      z.object({
        label: z.string().min(1).max(80).describe('Name of the slice, e.g. a sector or symbol'),
        weight: z
          .number()
          .min(0)
          .max(1)
          .describe('Share of the whole as a FRACTION 0..1 (0.35 = 35%) — never a percent'),
        color: z
          .string()
          .max(32)
          .optional()
          .describe('Optional CSS color for the slice; omitted, a palette rotation is used'),
      }),
    )
    .min(1)
    .max(20)
    .describe('1..20 slices; weights are fractions of the whole (they should sum to ~1)'),
});

/** Inline trend line -> `SparklineWidget.vue` (`Sparkline`). */
export const sparklineSpec = widgetBase.extend({
  type: z.literal('sparkline'),
  label: z.string().max(80).optional().describe('What the series is, e.g. "Revenue (5Y)"'),
  points: z
    .array(z.number())
    .min(2)
    .max(200)
    .describe('2..200 raw values, oldest first — the line is scaled to fit'),
  suffix: z.string().max(4).optional().describe('Unit shown after the latest value, e.g. "%"'),
});

/**
 * The OLD flat wire shape, `{ type: 'chart', chartType, data, title }` — every component field
 * beside the discriminator. It is the variant schemas above, unchanged, in their original order.
 *
 * The READ shape of persisted widgets, kept forever. THE FAILURE THIS PREVENTS: ~13,000
 * `tool-render_widget` outputs, ~3,000 `data-widget` parts and 1,031 board versions store this
 * shape; a reader that knows only `{ type, props }` blanks every one of them. Readers never parse
 * it themselves — they call `toWidgetSpec`, the one adapter.
 *
 * Writing it is already a TYPE ERROR at every writer, which accept only {@link WidgetSpec}:
 * `WidgetPart` (a `data-widget` part), `RenderWidgetOutput` (what `render_widget` returns, in
 * `@stoxly/shared-types`) and `BoardOpSchema` (a board's widget block). It appears only in the
 * READ types: `PersistedWidgetPart` and `PersistedRenderWidgetOutput`. No `@deprecated` tag: the
 * writer types are the rule, and the tag only flagged the legitimate read-side uses.
 */
export const LegacyWidgetSpec = z.discriminatedUnion('type', [
  statCardSpec,
  statGroupSpec,
  chartSpec,
  tableSpec,
  newsListSpec,
  calloutSpec,
  choicesSpec,
  scorecardSpec,
  gaugeSpec,
  rangeIndicatorSpec,
  thresholdBarSpec,
  allocationSpec,
  sparklineSpec,
]);
/** The persisted READ shape — see the schema of the same name. */
export type LegacyWidgetSpec = z.infer<typeof LegacyWidgetSpec>;

/**
 * Discriminator values — drives the renderer's `<component :is>` map.
 *
 * FROZEN wire vocabulary: additions only; an id is never repurposed or removed (persisted
 * threads reference these types forever).
 */
export type WidgetType = LegacyWidgetSpec['type'];

/** Exactly what the component for `T` accepts: the flat variant minus its discriminator. */
export type WidgetProps<T extends WidgetType> = Omit<
  Extract<LegacyWidgetSpec, { type: T }>,
  'type'
>;

/**
 * Each widget type's PROPS schema — the flat variant with `type` omitted, so nothing is written
 * twice. THE RUNG: the annotation makes a missing type, an extra key, or a key bound to another
 * type's schema (`gauge: statCardSpec.omit(…)`) a compile error.
 */
export const WIDGET_PROPS: { readonly [T in WidgetType]: z.ZodType<WidgetProps<T>> } = {
  stat_card: statCardSpec.omit({ type: true }),
  stat_group: statGroupSpec.omit({ type: true }),
  chart: chartSpec.omit({ type: true }),
  table: tableSpec.omit({ type: true }),
  news_list: newsListSpec.omit({ type: true }),
  callout: calloutSpec.omit({ type: true }),
  choices: choicesSpec.omit({ type: true }),
  scorecard: scorecardSpec.omit({ type: true }),
  gauge: gaugeSpec.omit({ type: true }),
  range_indicator: rangeIndicatorSpec.omit({ type: true }),
  threshold_bar: thresholdBarSpec.omit({ type: true }),
  allocation: allocationSpec.omit({ type: true }),
  sparkline: sparklineSpec.omit({ type: true }),
};

function variant<T extends WidgetType>(type: T) {
  return z.object({ type: z.literal(type), props: WIDGET_PROPS[type] });
}

/**
 * The canonical wire shape: `{ type, props }`, the same idea as `h(Component, props)` — `type`
 * selects the component and `props` is exactly what it accepts.
 *
 * THE FAILURE THIS PREVENTS: in the flat shape the discriminator shared a namespace with the
 * component's own fields, so no component could ever take a prop named `type`, and a model that
 * wrapped the fields in `props` was sending the wrong dialect. Every writer emits this shape; a
 * variant missing from this list makes `WidgetSpecOf<T>` `never` and the catalog stops compiling.
 */
export const WidgetSpec = z.discriminatedUnion('type', [
  variant('stat_card'),
  variant('stat_group'),
  variant('chart'),
  variant('table'),
  variant('news_list'),
  variant('callout'),
  variant('choices'),
  variant('scorecard'),
  variant('gauge'),
  variant('range_indicator'),
  variant('threshold_bar'),
  variant('allocation'),
  variant('sparkline'),
]);
/** Structurally `{ [T in WidgetType]: { type: T; props: WidgetProps<T> } }[WidgetType]`. */
export type WidgetSpec = z.infer<typeof WidgetSpec>;

/** The streamed `data-widget` payload — the WRITER's shape (canonical widget). */
export const WidgetPart = z.object({ id: z.string(), widget: WidgetSpec });
export type WidgetPart = z.infer<typeof WidgetPart>;

/**
 * A `data-widget` payload as READ back from `message.parts`: rows written before `{ type, props }`
 * carry the flat shape. Render it through `toWidgetSpec`, never directly.
 */
export interface PersistedWidgetPart {
  id: string;
  widget: WidgetSpec | LegacyWidgetSpec;
}
